/**
 * Bulk pools — three singleton card lots (Bulk / Holo Bulk / Premium Bulk).
 *
 * Bulk inventory isn't tracked card-by-card. Each category is a "pool" — a
 * single canonical card-lot row in the cards table identified by a fixed
 * Card Lot ID. Rip pulls, purchases, trade incoming/outgoing, sales, and gifts
 * that involve bulk all just bump Qty on the pool. Per-card value comes from
 * Settings; Total Basis and Current FMV are derived as Qty × unit value.
 */
import { DB } from "./db";
import { Settings } from "./prefs";
import { todayISO } from "../utils/format";
import type { Row } from "./types";

/** Canonical labels for the three pool classes. Order matters for display. */
export const BULK_CLASSES = ["Bulk", "Holo Bulk", "Premium Bulk", "$1-$5 Bulk", "$5-$10 Bulk"] as const;
export type BulkClass = (typeof BULK_CLASSES)[number];

export function isBulkClass(v: string | undefined): v is BulkClass {
  return !!v && (BULK_CLASSES as readonly string[]).includes(v);
}

/** Stable Card Lot ID per pool — used to look up the singleton. */
export function bulkPoolLotId(klass: BulkClass): string {
  if (klass === "Bulk")          return "BULK";
  if (klass === "Holo Bulk")     return "HOLO-BULK";
  if (klass === "Premium Bulk")  return "PREM-BULK";
  if (klass === "$1-$5 Bulk")    return "BULK-1-5";
  if (klass === "$5-$10 Bulk")   return "BULK-5-10";
  return "BULK";
}

/** Pull the per-card value for a pool from current Settings. */
export function bulkUnitValueFor(klass: BulkClass): number {
  const s = Settings.get();
  if (klass === "Bulk")          return Number(s.bulkUnitValue)        || 0;
  if (klass === "Holo Bulk")     return Number(s.holoBulkUnitValue)    || 0;
  if (klass === "Premium Bulk")  return Number(s.premiumBulkUnitValue) || 0;
  if (klass === "$1-$5 Bulk")    return Number(s.bulk1to5UnitValue)    || 0;
  if (klass === "$5-$10 Bulk")   return Number(s.bulk5to10UnitValue)   || 0;
  return 0;
}

/** Return the existing pool lot for a class, or null if none yet. */
export function getBulkPool(klass: BulkClass): Row | null {
  const id = bulkPoolLotId(klass);
  return DB.table("cards").find(r => r["Card Lot ID"] === id) || null;
}

/** Find the user's pool lot for a class, creating it if missing. */
export function getOrCreateBulkPool(klass: BulkClass): Row {
  const existing = getBulkPool(klass);
  if (existing) return existing;
  const unitBasis = bulkUnitValueFor(klass);
  const newLot: Row = {
    "Card Lot ID":      bulkPoolLotId(klass),
    "Game":             "Other",          // bulk is mixed; "Other" is the neutral game
    "Card Name":        klass,            // human-readable label = the class name
    "Set":              "",
    "Card #":           "",
    "Inventory Class":  klass,
    "Condition/Grade":  "",
    "Status":           "Available",
    "Qty":              0,
    "Acq Date":         todayISO(),
    "Acq Type":         "Bulk pool",
    "Valuation Source": "Settings",
    "Unit Basis":       unitBasis,
    "Total Basis":      0,
    "Current FMV":      0,
    "Unrealized G/L":   0,
    "Source ID":        "",
    "Notes":            "Auto-managed bulk pool. Qty adjusts with rip/purchase/trade/sale operations; per-card value comes from Configs → Defaults.",
  };
  DB.add("cards", newLot);
  return newLot;
}

/**
 * Adjust a pool by `delta` cards. Cost handling (average-cost method):
 *   - `cost` given (a priced purchase): that exact amount is added to the pool's
 *     running Total Basis.
 *   - adding without a cost (rip pull / trade-in): falls back to the set value.
 *   - removing (sale / trade-out): leaves at the pool's current average cost.
 * FMV is always Qty x the set value from Settings, tracked separately from cost.
 */
export function addToBulkPool(klass: BulkClass, delta: number, cost?: number): Row {
  const pool = getOrCreateBulkPool(klass);
  const beforeQty = Number(pool["Qty"]) || 0;
  const beforeBasis = Number(pool["Total Basis"]) || 0;
  const setValue = bulkUnitValueFor(klass);
  const dQty = Number(delta) || 0;

  let dCost: number;
  if (cost != null) dCost = Number(cost) || 0;
  else if (dQty >= 0) dCost = +(dQty * setValue).toFixed(2);
  else dCost = -(+(((beforeQty > 0 ? beforeBasis / beforeQty : setValue)) * Math.abs(dQty)).toFixed(2));

  const afterQty = Math.max(0, beforeQty + dQty);
  const afterBasis = Math.max(0, +(beforeBasis + dCost).toFixed(2));
  const unitBasis = afterQty > 0 ? +(afterBasis / afterQty).toFixed(2) : 0;
  const fmv = +(afterQty * setValue).toFixed(2);
  Object.assign(pool, {
    "Qty":            afterQty,
    "Unit Basis":     unitBasis,
    "Total Basis":    afterBasis,
    "Current FMV":    fmv,
    "Unrealized G/L": +(fmv - afterBasis).toFixed(2),
  });
  DB.update("cards", "Card Lot ID", pool["Card Lot ID"], pool);
  return pool;
}

/** Convenience: decrement by `qty` (positive number). */
export function removeFromBulkPool(klass: BulkClass, qty: number): Row {
  return addToBulkPool(klass, -Math.abs(Number(qty) || 0));
}

/**
 * Re-sync the basis/FMV on every existing pool to current Settings values.
 * Called after the Configs Defaults form saves so changes to the per-card
 * values propagate to the pool rows immediately. Pools that don't exist yet
 * are left alone (lazy-create on first add).
 */
export function refreshAllBulkPoolBasis(): void {
  for (const klass of BULK_CLASSES) {
    const pool = getBulkPool(klass);
    if (!pool) continue;
    const qty = Number(pool["Qty"]) || 0;
    const totalBasis = Number(pool["Total Basis"]) || 0;
    const fmv = +(qty * bulkUnitValueFor(klass)).toFixed(2);
    Object.assign(pool, {
      "Current FMV":    fmv,
      "Unrealized G/L": +(fmv - totalBasis).toFixed(2),
    });
    DB.update("cards", "Card Lot ID", pool["Card Lot ID"], pool);
  }
}

/** True when a card row is one of the three bulk pool singletons. */
export function isBulkPoolRow(row: Row | undefined): boolean {
  if (!row) return false;
  const id = row["Card Lot ID"];
  return !!id && BULK_CLASSES.some(k => bulkPoolLotId(k) === id);
}
